/**
 * 导航栏标题 i18n：按路由解析文案，并同步 uni / 云闪付原生顶栏。
 *
 * @module locale/navigation-bar
 */

import { translate } from '@/locale/index'
import { ensureMiscLocales } from '@/misc/i18n/ensure-locales'

// --- 类型定义 ---

type NavTitleResolver = (query: Record<string, string>) => string | null

interface NavTitleConfig {
  key?: string
  resolve?: NavTitleResolver
}

// --- 常量与变量 ---

/**
 * 路由 path（无 query）→ i18n 配置。
 * 覆盖 pages.json / route 块中的静态占位标题（接入 i18n 后多为英文占位）。
 */
const NAV_TITLE_I18N_BY_PATH: Record<string, NavTitleConfig> = {
  'plane/index': { key: 'plane.nav.home' },
  'plane/plane_area/plane_area': { key: 'plane.nav.area' },
  'plane/plane_change/plane_change': { key: 'plane.nav.change' },
  'plane/plane_detail/plane_detail': { key: 'plane.nav.detail' },
  'plane/plane_guide/plane_guide': { key: 'plane.nav.guide' },
  'plane/plane_list/plane_list': { key: 'plane.nav.list' },
  'plane/plane_low_price/plane_low_price': { key: 'plane.nav.lowPrice' },
  'plane/plane_luggage/index': { key: 'plane.nav.luggage' },
  'plane/plane_order/plane_order': { key: 'plane.nav.orderFill' },
  'plane/plane_remind/index': { key: 'plane.nav.remindList' },
  'plane/plane_return/plane_return': { key: 'plane.nav.return' },
  'plane/plane_select_address/plane_select_address': {
    resolve: (query) => (query.type === '0' ? 'plane.nav.selectFrom' : 'plane.nav.selectTo'),
  },
  'plane/plane_treaty/plane_treaty': { key: 'plane.nav.treaty' },
  'plane/plane_week/plane_week': { key: 'plane.nav.week' },
  'plane/plane_passengers/passengers_add/passengers_add': { key: 'plane.nav.passengerAdd' },
  'plane/plane_remind/add_remind/add_remind': { key: 'plane.nav.remindAdd' },
  'plane/plane_remind/flight_list/flight_list': { key: 'plane.nav.remindFlights' },

  'hotel/index': { key: 'hotel.nav.home' },
  'hotel/hotel_cart/hotel_cart': { key: 'hotel.nav.cart' },
  'hotel/hotel_detail/hotel_detail': { key: 'hotel.nav.detail' },
  'hotel/hotel_evaluate/hotel_evaluate': { key: 'hotel.nav.evaluate' },
  'hotel/hotel_facilities/hotel_facilities': { key: 'hotel.nav.facilities' },
  'hotel/hotel_highlights/hotel_highlights': { key: 'hotel.nav.highlights' },
  'hotel/hotel_list/hotel_list': { key: 'hotel.nav.list' },
  'hotel/hotel_order/hotel_order': { key: 'hotel.nav.order' },
  'hotel/hotel_search/hotel_search': { key: 'hotel.nav.search' },
  'hotel/international_hotel/international_hotel_search': { key: 'hotel.nav.intlCitySearch' },
  'hotel/international_hotel/order_detail': { key: 'hotel.nav.intlOrderDetail' },

  'tourism/index': { key: 'tourism.nav.home' },
  'tourism/tourism_detail/tourism_detail': { key: 'tourism.nav.detail' },
  'tourism/tourism_list/tourism_list': { key: 'tourism.nav.list' },
  'tourism/tourism_order/tourism_order': { key: 'tourism.nav.order' },
  'tourism/tourism_select_city/tourism_select_city': { key: 'tourism.nav.selectCity' },

  'pageB/train/index': { key: 'train.nav.home' },
  'pageB/train/train_list': { key: 'train.nav.list' },
  'pageB/train/train_detail': { key: 'train.nav.detail' },
  'pageB/train/train_order': { key: 'train.nav.orderFill' },
  'pageB/train/train_order_detail': { key: 'train.nav.orderDetail' },
  'pageB/train/train_change': { key: 'train.nav.change' },
  'pageB/train/train_change_order_detail': { key: 'train.nav.changeOrderDetail' },
  'pageB/train/train_refund': { key: 'train.nav.refundApply' },
  'pageB/train/train_refund_progress': { key: 'train.nav.refundProgress' },
  'pageB/international_plane/international_plane_order_detail': { key: 'plane.nav.intlOrderDetail' },
  'pageB/international_plane/international_plane_detail': { key: 'plane.nav.intlDetail' },
  'pageB/international_plane/international_plane_list': { key: 'plane.nav.intlList' },
  'pageB/international_plane/international_plane_change': { key: 'plane.nav.intlChange' },
  'pageB/international_plane/international_plane_change_detail': { key: 'plane.nav.intlChangeDetail' },
  'pageB/international_plane/international_plane_change_list': { key: 'plane.nav.intlChangeList' },
  'pageB/international_plane/international_plane_progress': { key: 'plane.nav.intlProgress' },
  'pageB/international_plane/international_plane_refund_detail': { key: 'plane.nav.intlRefundDetail' },
  'pageB/international_plane/international_plane_refund': { key: 'plane.nav.intlRefund' },

  'insurance/index': { key: 'insurance.nav.home' },
  'insurance/insurance_detail/insurance_detail': { key: 'insurance.nav.detail' },

  'misc/pages/order-list/index': { key: 'misc.nav.myOrders' },
  'misc/pages/order-fill/index': { key: 'misc.nav.orderFill' },
  'misc/pages/order-search/index': { key: 'misc.nav.orderSearch' },
  'misc/pages/travel-hub/index': { key: 'misc.nav.travelHub' },

  'pages/order/apply_refund/apply_refund': { key: 'order.applyRefund.nav.title' },
  'pages/order/confirm_refund/confirm_refund': { key: 'order.confirmRefund.nav.title' },
  'pages/order/invoice/invoice': { key: 'order.invoice.nav.title' },
  'pages/order/invoice_detail/invoice_detail': { key: 'order.invoiceDetail.nav.title' },
  'pages/order/invoice_success/invoice_success': { key: 'order.invoiceSuccess.nav.title' },
  'pages/order/pay/pay': { key: 'order.pay.nav.title' },
  'pages/order/refund/refund': { key: 'order.refund.nav.title' },
  'pages/order/refund/refund_plane/refund_plane': {
    resolve: (query) =>
      query.isInternational === 'true' && query.isChange === 'true'
        ? 'order.refundPlane.nav.selectChangeReason'
        : 'order.refundPlane.nav.selectRefundReason',
  },
  'pages/order/refund_progress/refund_progress': { key: 'order.refundProgress.nav.title' },
  'pages/order/refund_progress/progress_plane/progress_plane': {
    key: 'order.refundProgressPlane.nav.title',
  },

  'pageC/combinedOrder/index': { key: 'combinedOrder.nav.confirmTitle' },
  'pageC/combinedOrder/detail': { key: 'combinedOrder.nav.detailTitle' },
  'pageC/order_detail/order_detail': { key: 'orderDetail.nav.title' },

  'pageC/activity/yx/index': { key: 'activity.yx.nav.home' },
  'pageC/activity/yx/detailed_rules': { key: 'activity.yx.nav.rules' },
}

// --- 函数 ---

function parseRouteQuery(fullPath: string): Record<string, string> {
  const queryString = fullPath.split('?')[1] || ''
  const query: Record<string, string> = {}
  if (!queryString) {
    return query
  }
  for (const segment of queryString.split('&')) {
    const [rawKey, rawValue = ''] = segment.split('=')
    if (!rawKey) {
      continue
    }
    try {
      query[decodeURIComponent(rawKey)] = decodeURIComponent(rawValue)
    }
    catch {
      query[rawKey] = rawValue
    }
  }
  return query
}

function normalizeRoutePath(fullPath: string): string {
  let pathOnly = (fullPath || '').split('?')[0]
  const publicBase = import.meta.env.VITE_APP_PUBLIC_BASE || '/'
  if (publicBase && publicBase !== '/' && pathOnly.startsWith(publicBase)) {
    pathOnly = pathOnly.slice(publicBase.length)
  }
  return pathOnly.replace(/^\/+/, '').replace(/\/+$/, '')
}

/** 延迟加载 base.js，避免与 getNavigationBarTitleByPath 循环引用 */
function syncUpAppNavigationBarTitle(title: string): void {
  queueMicrotask(() => {
    import('@/utils/base').then(({ isUnionPayHostEnv, setUpAppNavTitle }) => {
      // 云网不走 upsdk.setTitleStyle，setUpAppNavTitle 内已分支为仅 setNavigationBarTitle
      if (isUnionPayHostEnv()) {
        setUpAppNavTitle(title)
      }
    })
  })
}

/** uni 导航栏 + 云闪付/云网 upsdk 原生顶栏同步标题 */
export function setAppNavigationBarTitle(title: string): void {
  if (!title) {
    return
  }
  uni.setNavigationBarTitle({ title })
  syncUpAppNavigationBarTitle(title)
}

/**
 * 按路由解析 i18n 导航栏标题。
 *
 * @returns 有 i18n 配置时返回翻译文案，否则 null（回退 pages.json 静态标题）
 */
export function resolveI18nNavigationBarTitle(fullPath: string): string | null {
  ensureMiscLocales()
  const normalizedPath = normalizeRoutePath(fullPath)
  const config = NAV_TITLE_I18N_BY_PATH[normalizedPath]
  if (!config) {
    return null
  }

  let i18nKey = config.key
  if (config.resolve) {
    const resolvedKey = config.resolve(parseRouteQuery(fullPath))
    if (resolvedKey) {
      i18nKey = resolvedKey
    }
  }
  if (!i18nKey) {
    return null
  }

  return translate(i18nKey)
}
